import { Modal, Form } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "./Controls/Button";
import { Input } from "./Controls/Input";
import { HeaderText } from "./Controls/HeaderText";
import { MoneyInput } from "./Controls/MoneyInput";
import { showMoney, toCents, cents } from "./Data/Money";
import { OrderInfo } from "./Data/ApiTypes";
import { Button as BsButton } from "react-bootstrap";

interface OrderAdjustModalProps {
    order:       OrderInfo;
    parkerEmail: string;
    show:        boolean;
    setShow:     ( show: boolean                               ) => void;
    onSave:      ( data: OrderAdjustForm, sendInvoice: boolean ) => void;
}

export interface OrderAdjustForm {
    newTotalDebt: number;
    reason:       string;
}

export function InvoiceOrderAdjustModal( { order, parkerEmail, show, setShow, onSave }: OrderAdjustModalProps ) {
    const currentDebtCents = toCents( order.balance.totalDebt );

    const { register, watch, setValue, formState: { errors }, reset, handleSubmit } = useForm<OrderAdjustForm>( {
        defaultValues: {
            newTotalDebt: currentDebtCents,
            reason:       "",
        },
    } );

    const amountWrapperRef                        = useRef<HTMLDivElement>( null );
    const [confirming, setConfirming]             = useState( false );
    const [pendingData, setPendingData]           = useState<OrderAdjustForm | null>( null );

    useEffect( () => {
        if ( !show ) return;
        reset( { newTotalDebt: currentDebtCents, reason: "" } );
        setConfirming( false );
        setPendingData( null );
        setTimeout( () => {
            const el = amountWrapperRef.current?.querySelector<HTMLInputElement>( 'input:not([type="number"])' );
            el?.focus();
        }, 0 );
    }, [show] );

    function clearAmount() {
        setValue( "newTotalDebt", 0 );
        setTimeout( () => {
            const el = amountWrapperRef.current?.querySelector<HTMLInputElement>( 'input:not([type="number"])' );
            el?.focus();
        }, 0 );
    }

    function handleFormSubmit( data: OrderAdjustForm ) {
        setPendingData( data );
        setConfirming( true );
    }

    function handleSave( sendInvoice: boolean ) {
        if ( !pendingData ) return;
        onSave( pendingData, sendInvoice );
        reset();
        setShow( false );
    }

    const newTotalDebtRaw = watch( "newTotalDebt" );
    const reason          = watch( "reason" );
    const totalPaidCents  = toCents( order.balance.totalPaid );

    const newTotalDebtCents = newTotalDebtRaw ?? 0;
    const deltaCents  = newTotalDebtCents - currentDebtCents;
    const previewText = !isNaN( newTotalDebtCents ) && newTotalDebtCents > 0 && newTotalDebtCents >= totalPaidCents && deltaCents !== 0
        ? `Adjustment: ${deltaCents > 0 ? "+" : ""}${showMoney( cents( deltaCents ) )}`
        : null;

    const confirmDebt  = pendingData ? pendingData.newTotalDebt : 0;
    const confirmDelta = confirmDebt - currentDebtCents;

    if ( confirming && pendingData ) {
        return <Modal show={show} centered onEscapeKeyDown={() => setShow( false )}>
            <Modal.Header className="fs-5">
                <HeaderText>Confirm Adjustment</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <table className="table table-sm mb-0">
                    <tbody>
                        <tr>
                            <td className="text-secondary">Current Amount</td>
                            <td className="text-end">{showMoney( order.balance.totalDebt )}</td>
                        </tr>
                        <tr>
                            <td className="text-secondary">New Amount</td>
                            <td className="text-end fw-semibold">{showMoney( cents( confirmDebt ) )}</td>
                        </tr>
                        <tr className={confirmDelta < 0 ? "text-success" : "text-danger"}>
                            <td>Adjustment</td>
                            <td className="text-end fw-bold">
                                {confirmDelta > 0 ? "+" : ""}{showMoney( cents( confirmDelta ) )}
                            </td>
                        </tr>
                        <tr>
                            <td className="text-secondary">Reason</td>
                            <td className="text-end">{pendingData.reason}</td>
                        </tr>
                    </tbody>
                </table>
            </Modal.Body>
            <Modal.Footer className="d-flex flex-column gap-2">
                <div className="d-flex w-100 gap-2">
                    <Button className="flex-grow-1" onClick={() => setConfirming( false )}>← Back</Button>
                    <Button className="flex-grow-1 btn-main" onClick={() => handleSave( false )}>Save</Button>
                </div>
                <Button className="w-100" onClick={() => handleSave( true )}>
                    Save &amp; Send Invoice to Parker
                    <div className="small text-muted mt-1">{parkerEmail}</div>
                </Button>
            </Modal.Footer>
        </Modal>;
    }

    return <Modal show={show} centered onEscapeKeyDown={() => setShow( false )}>
        <Form onSubmit={handleSubmit( handleFormSubmit )}>
            <Modal.Header className="fs-5">
                <HeaderText>Adjust Service Amount</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <p className="p-2 text-secondary">
                    Set the service amount to a new value.
                </p>
                <Input
                    inputClassName="text-end"
                    type="text"
                    label="Current Service Amount"
                    disabled
                    value={showMoney( order.balance.totalDebt )}
                    placeholder=""
                    explanation="The current total debt on this service" />

                <div ref={amountWrapperRef}>
                    <MoneyInput
                        label="New Amount"
                        inputClassName="text-end"
                        explanation={previewText ?? "Enter the new total amount for this service"}
                        min="0"
                        value={newTotalDebtRaw}
                        append={
                            <BsButton variant="outline-secondary mx-2" size="sm" type="button" onClick={clearAmount}>
                                 Clear
                            </BsButton>
                        }
                        { ...register( "newTotalDebt", {
                            valueAsNumber: true,
                            required: "Amount is required",
                            validate: ( value: number ) => {
                                if( isNaN( value ) || value < 0 ) return "Please enter a valid amount";
                                if( value < totalPaidCents ) return "Amount would result in a negative balance";
                                return true;
                            },
                        } ) }
                        error={errors.newTotalDebt} />
                </div>

                <Input
                    type="text"
                    label="Reason"
                    maxLength={64}
                    placeholder="Accidentally entered 10 instead of 100"
                    explanation={`Why is an adjustment needed? ${reason.length}/64`}
                    { ...register( "reason", { required: "Reason is required" } ) }
                    error={errors.reason} />
            </Modal.Body>
            <Modal.Footer className="d-flex">
                <Button className="flex-grow-1" onClick={() => setShow( false )}>Cancel</Button>
                <Button className="flex-grow-1 btn-main" type="submit" disabled={deltaCents === 0}>Review</Button>
            </Modal.Footer>
        </Form>
    </Modal>;
}
